import { IconEdit, IconTrash } from "@tabler/icons-react";
import { partnerApi } from "apis/partnerApi";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomTable from "components/ui/CustomTable";
import CustomText from "components/ui/CustomText/CustomText";
import GroupActions from "components/ui/GroupActions";
import { ActionItem } from "components/ui/GroupActions/GroupActions";
import { STATUS } from "constants/common/common";
import { PARTNER_TYPE_LABEL } from "constants/common/partner";
import {
  PartnerParamsType,
  PartnerResponseType,
} from "constants/types/partner";
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "mantine-react-table";
import { FORM_IDS } from "pages/Partner/config";
import { FC, useMemo, useRef, useCallback } from "react";
import { useNotification } from "utils/hooks";
import {
  usePartners,
  getPartnerListKey,
} from "utils/hooks/request/usePartners";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import { useSWRConfig } from "swr";
import { Loading } from "components";
import EditForm from "../EditForm/EditForm";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";
import { useUsers } from "utils/hooks/request/useUsers";

interface PartnerTableProps {
  params: PartnerParamsType;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const ICON_SIZE = 18;

const PartnerTable: FC<PartnerTableProps> = ({
  params,
  onPageChange,
  onLimitChange,
}) => {
  const { notification } = useNotification();
  const { mutate } = useSWRConfig();
  const ability = useAbility();

  const {
    partners,
    pagination,
    isLoading,
    mutate: localMutate,
  } = usePartners(params);

  const { users } = useUsers();
  const userNameMap = useMemo(
    () =>
      (users ?? []).reduce<Record<string, string>>((acc, u) => {
        acc[u.id] = u.fullname;
        return acc;
      }, {}),
    [users],
  );

  const { openCrudModal, closeAll, closeCrudModal } = useAppModal();
  const activeModalId = useRef<string | null>(null);

  const handleCloseModal = useCallback(() => {
    if (activeModalId.current) {
      closeCrudModal(activeModalId.current);
      activeModalId.current = null;
    }
  }, [closeCrudModal]);

  const handleDeletePartner = useCallback(
    async (partnerId: string) => {
      try {
        await partnerApi.delete(partnerId);
        notification.success({ title: "Xóa đối tác thành công" });
        closeAll();
        localMutate();
        onPageChange(1);
      } catch {
        notification.error({ title: "Xóa đối tác thất bại" });
      }
    },
    [notification, closeAll, localMutate, onPageChange],
  );

  const handleOpenDeleteModal = useCallback(
    (partnerId: string) => {
      openCrudModal(
        <CustomText variant="section-title">Xác nhận xóa đối tác</CustomText>,
        {
          mode: "delete",
          formId: FORM_IDS.DELETE,
          onConfirmDelete: async () => await handleDeletePartner(partnerId),
        },
      );
    },
    [openCrudModal, handleDeletePartner],
  );

  const handleOpenEditModal = useCallback(
    (partner: PartnerResponseType) => {
      const modalId = openCrudModal(
        <CustomText variant="section-title">Chỉnh sửa đối tác</CustomText>,
        {
          mode: "edit",
          formId: FORM_IDS.EDIT,
          children: (
            <EditForm
              partner={partner}
              mutate={() => mutate(getPartnerListKey(params))}
              onClose={handleCloseModal}
            />
          ),
          submitText: "Cập nhật",
        },
        750,
      );
      activeModalId.current = modalId;
    },
    [openCrudModal, mutate, params, handleCloseModal],
  );

  const partnerActions: ActionItem<PartnerResponseType>[] = useMemo(
    () => [
      {
        key: "edit",
        label: "Chỉnh sửa",
        icon: <IconEdit size={ICON_SIZE} />,
        color: "orange",
        permission: "partner:update",
        onClick: (row) => handleOpenEditModal(row),
      },
      {
        key: "delete",
        label: "Xóa",
        icon: <IconTrash size={ICON_SIZE} />,
        color: "red",
        permission: "partner:delete",
        onClick: (row) => handleOpenDeleteModal(row.id),
      },
    ],
    [handleOpenEditModal, handleOpenDeleteModal],
  );

  const hasActions = useMemo(
    () =>
      partnerActions.some((a) =>
        canAccessRequirement(ability, a.permission, true),
      ),
    [partnerActions, ability],
  );

  const columns: MRT_ColumnDef<PartnerResponseType>[] = [
    {
      id: "stt",
      header: "STT",
      size: 5,
      grow: false,
      Cell: ({ row }) => row.index + 1,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "code",
      header: "Mã đối tác",
    },
    {
      accessorKey: "name",
      header: "Tên đối tác",
    },
    // {
    //   accessorKey: "type",
    //   header: "Loại",
    //   Cell: ({ cell }) => PARTNER_TYPE_LABEL[cell.getValue<string>()] ?? cell.getValue<string>(),
    // },
    {
      accessorKey: "region.name",
      header: "Địa bàn",
    },
    {
      accessorKey: "phone",
      header: "Số điện thoại",
    },
    {
      id: "contactPerson",
      header: "Người phụ trách",
      Cell: ({ row }) => {
        const { contactPersonId, contactPerson } = row.original;
        if (contactPersonId)
          return userNameMap[contactPersonId] ?? contactPersonId;
        return contactPerson || "—";
      },
    },
    {
      accessorKey: "taxCode",
      header: "Mã số thuế",
    },
    {
      accessorKey: "isActive",
      header: "Trạng thái",
      grow: false,
      Cell: ({ cell }) =>
        cell.getValue() ? (
          <CustomBadge label={STATUS.ACTIVE_LABEL} color="green" />
        ) : (
          <CustomBadge label={STATUS.INACTIVE_LABEL} color="gray" />
        ),
    },
    ...(hasActions
      ? [
          {
            id: "actions",
            header: "Thao tác",
            enableSorting: false,
            enableColumnFilter: false,
            size: 50,
            grow: false,
            enableResizing: false,
            mantineTableHeadCellProps: { align: "center" },
            mantineTableBodyCellProps: { align: "center" },
            Cell: ({ row }: any) => (
              <GroupActions row={row.original} actions={partnerActions} />
            ),
          } satisfies MRT_ColumnDef<PartnerResponseType>,
        ]
      : []),
  ];

  const handleChange = useCallback(
    (state: {
      page: number;
      limit: number;
      filters?: MRT_ColumnFiltersState;
    }) => {
      if (pagination && state.page !== pagination.page) {
        onPageChange(state.page);
      } else if (pagination && state.limit !== pagination.limit) {
        onLimitChange(state.limit);
      }
    },
    [onPageChange, onLimitChange, pagination],
  );

  return (
    <Loading variant="overlay" visible={isLoading}>
      <CustomTable
        columns={columns}
        data={partners ?? []}
        pagination={pagination!}
        onStateChange={handleChange}
      />
    </Loading>
  );
};

export default PartnerTable;
